import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import './BottomSheet.css';

export type Detent = 'peek' | 'half' | 'full';

interface Props {
    detent: Detent;
    onDetentChange: (d: Detent) => void;
    children: ReactNode;
    /** Announced as the sheet's accessible name. */
    label: string;
}

const FRACTION: Record<Detent, number> = { peek: 0.18, half: 0.52, full: 0.92 };

/**
 * A draggable bottom sheet with three detents.
 *
 * One surface holds the result list and, when you pick something, the detail —
 * rather than a list panel and a separate detail panel competing for the same
 * space. On desktop this becomes a fixed left rail (see the CSS), which is why
 * the drag handlers bail out above 900px.
 */
export function BottomSheet({ detent, onDetentChange, children, label }: Props) {
    const sheetRef = useRef<HTMLElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const drag = useRef<{ startY: number; startH: number; pointerId: number } | null>(null);
    const [dragHeight, setDragHeight] = useState<number | null>(null);

    const heightFor = useCallback((d: Detent) => Math.round(window.innerHeight * FRACTION[d]), []);

    const isDesktop = () => window.matchMedia('(min-width: 900px)').matches;

    const onPointerDown = (e: React.PointerEvent) => {
        if (isDesktop()) return;
        // Dragging down from the top of an already-scrolled list should scroll,
        // not resize; only start a drag when the list is at its top.
        if (e.target instanceof Element && e.target.closest('.sheet__scroll') && (scrollRef.current?.scrollTop ?? 0) > 0) return;
        drag.current = { startY: e.clientY, startH: sheetRef.current?.offsetHeight ?? heightFor(detent), pointerId: e.pointerId };
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const d = drag.current;
        if (!d) return;
        const next = Math.min(window.innerHeight * 0.94, Math.max(90, d.startH - (e.clientY - d.startY)));
        setDragHeight(next);
    };

    const onPointerUp = () => {
        const d = drag.current;
        drag.current = null;
        if (d === null || dragHeight === null) { setDragHeight(null); return; }
        // Snap to whichever detent is nearest where the finger let go.
        const nearest = (Object.keys(FRACTION) as Detent[])
            .reduce((best, k) => (Math.abs(heightFor(k) - dragHeight) < Math.abs(heightFor(best) - dragHeight) ? k : best), 'peek' as Detent);
        setDragHeight(null);
        onDetentChange(nearest);
    };

    // Reset the scroll position when the sheet collapses, so reopening it does
    // not reveal the middle of a list.
    useEffect(() => {
        if (detent === 'peek' && scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [detent]);

    const cycle = () => onDetentChange(detent === 'full' ? 'peek' : detent === 'half' ? 'full' : 'half');

    return (
        <section
            ref={sheetRef}
            className={`sheet sheet--${detent}`}
            style={dragHeight !== null ? { blockSize: `${dragHeight}px`, transition: 'none' } : undefined}
            aria-label={label}
        >
            <div
                className="sheet__grip"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <button
                    type="button"
                    className="sheet__grab"
                    onClick={cycle}
                    aria-label={detent === 'full' ? 'Collapse list' : 'Expand list'}
                    aria-expanded={detent !== 'peek'}
                />
            </div>
            <div className="sheet__scroll" ref={scrollRef}>{children}</div>
        </section>
    );
}
